import { Form, Formik, useField } from 'formik';
import { User as UserIcon } from 'lucide-react';
import { useRouter } from '@uirouter/react';
import * as Yup from 'yup';

import { notifySuccess } from '@/portainer/services/notifications';
import { isPureAdmin } from '@/portainer/users/user.helpers';
import { useUpdateUserMutation } from '@/portainer/users/queries/useUpdateUserMutation';
import { useUsers } from '@/portainer/users/queries/useUsers';
import { Role, User } from '@/portainer/users/types';

import { Widget } from '@@/Widget';
import { FormControl } from '@@/form-components/FormControl';
import { FormActions } from '@@/form-components/FormActions';
import { Input } from '@@/form-components/Input';
import { SwitchField } from '@@/form-components/SwitchField';
import { ModalType } from '@@/modals';
import { confirm } from '@@/modals/confirm';
import { buildConfirmButton } from '@@/modals/utils';

interface FormValues {
  username: string;
  isAdmin: boolean;
}

const validation = Yup.object({
  username: Yup.string().required('Username is required').trim(),
  isAdmin: Yup.boolean().required(),
});

export function UserForm({ user }: { user: User }) {
  const router = useRouter();
  const updateMutation = useUpdateUserMutation();
  const adminCountQuery = useUsers(
    true,
    0,
    isPureAdmin(user),
    (users) => users.filter(isPureAdmin).length
  );
  const isLastAdmin = isPureAdmin(user) && adminCountQuery.data === 1;

  const initialValues: FormValues = {
    username: user.Username,
    isAdmin: isPureAdmin(user),
  };

  return (
    <Widget>
      <Widget.Title icon={UserIcon} title="User details" />
      <Widget.Body>
        <Formik<FormValues>
          initialValues={initialValues}
          validationSchema={validation}
          enableReinitialize
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, isValid, dirty }) => (
            <Form className="form-horizontal">
              <UsernameField />

              <div className="form-group">
                <div className="col-sm-12">
                  <SwitchField
                    label="Administrator"
                    tooltip={
                      isLastAdmin
                        ? 'This is the only administrator. Make another user an administrator before removing this role.'
                        : 'Administrators have access to Portainer settings management as well as full control over all defined environments and their resources.'
                    }
                    labelClass="col-sm-3 col-lg-2"
                    checked={values.isAdmin}
                    disabled={isLastAdmin}
                    onChange={(checked) => setFieldValue('isAdmin', checked)}
                    data-cy="user-administrator-switch"
                  />
                </div>
              </div>

              <FormActions
                submitLabel="Save"
                loadingText="Saving"
                isLoading={updateMutation.isLoading}
                isValid={isValid && dirty}
                data-cy="user-details-save-button"
              />
            </Form>
          )}
        </Formik>
      </Widget.Body>
    </Widget>
  );

  async function handleSubmit(values: FormValues) {
    if (values.username !== user.Username) {
      const confirmed = await confirm({
        title: 'Are you sure?',
        modalType: ModalType.Warn,
        message: `Are you sure you want to rename the user ${user.Username} to ${values.username}?`,
        confirmButton: buildConfirmButton('Update'),
      });

      if (!confirmed) {
        return;
      }
    }

    updateMutation.mutate(
      {
        userId: user.Id,
        payload: {
          username: values.username,
          ...(values.isAdmin !== initialValues.isAdmin && {
            role: values.isAdmin ? Role.Admin : Role.Standard,
          }),
        },
      },
      {
        onSuccess() {
          notifySuccess('Success', 'User successfully updated');
          router.stateService.reload();
        },
      }
    );
  }
}

function UsernameField() {
  const [{ name, onBlur, onChange, value }, { error }] =
    useField<string>('username');

  return (
    <FormControl
      inputId="user-username"
      label="Username"
      required
      errors={error}
    >
      <Input
        id="user-username"
        name={name}
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        data-cy="user-username-input"
      />
    </FormControl>
  );
}
